// The session view's arithmetic over logged sets (walkthroughs 4–7): their
// order, merging what the server answers into what's on screen, what the
// set queue sends, how a failed send is read, and the finish screen's sums —
// fractional sets per muscle and each main lift's estimated max today.
// Pure: nothing here touches React, storage or the network.
import { parseApiError } from "../../../services/apiError";
import { MUSCLES, muscleLabel } from "./format";
import { E1RM_MAX_REPS, UNKNOWN_RIR, epley } from "./steps";

// Walkthrough 7 / D12: feedback is asked for muscles with ≥ 2 sets.
export const FEEDBACK_MIN_SETS = 2;
// A queued set younger than this is sent without its time, so the server's
// clock dates it: the API refuses a time more than 2 minutes ahead of its
// own (constants.FUTURE_SKEW), and a phone's clock can run fast. An older
// one (sent after the connection came back) keeps the time it was done.
export const BACKDATE_AFTER_MS = 5 * 60 * 1000;
// How long a set that couldn't reach mono waits before it is sent again on
// its own (it also goes at once on the next log, and when the page or the
// connection comes back).
export const RETRY_MS = 15 * 1000;
// A throttled send waits this long when the server doesn't say.
export const THROTTLE_MS = 30 * 1000;
// backend/training/constants.py MAX_SETS_PER_BATCH: the API takes at most
// this many sets in one request (more is a 400 that names no set), so the
// queue sends in batches of this size.
export const MAX_SETS_PER_BATCH = 50;
// backend/training/constants.py STALE_AFTER (D18): an open session idle
// this long closes itself at its last activity.
export const SESSION_IDLE_MS = 6 * 60 * 60 * 1000;
// What the queue keeps beside a set that is only for this device (the
// set's line, for saying which set a refusal was about): never sent.
const LOCAL_FIELDS = ["label"];

const MUSCLE_ORDER = new Map(MUSCLES.map((m, i) => [m.code, i]));
const tidy = (value) => Math.round(value * 100) / 100;

const time = (set) => {
  const t = Date.parse(set?.performed_at ?? "");
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

/** Sets in the API's order: when they were done, then id. */
export function sortSets(sets) {
  return [...(sets ?? [])].sort(
    (a, b) => time(a) - time(b) || (a.id ?? Number.MAX_SAFE_INTEGER) - (b.id ?? Number.MAX_SAFE_INTEGER),
  );
}

/** `current` with `incoming` added — a set already there (same id) is replaced. */
export function mergeSets(current, incoming) {
  const byId = new Map((current ?? []).map((set) => [set.id, set]));
  for (const set of incoming ?? []) byId.set(set.id, set);
  return sortSets([...byId.values()]);
}

/** `sets` without the one with `id`. */
export function withoutSet(sets, id) {
  return (sets ?? []).filter((set) => set.id !== id);
}

/** `{exercise key: sets}` over every list given (server rows and queued ones). */
export function countByExercise(...lists) {
  const counts = {};
  for (const list of lists) {
    for (const set of list ?? []) counts[set.exercise] = (counts[set.exercise] ?? 0) + 1;
  }
  return counts;
}

/**
 * What the queue sends for a set: as it was queued, without `performed_at`
 * while the set is younger than BACKDATE_AFTER_MS (see above), and without
 * what the queue keeps only for this device (its `label`).
 */
export function wireSet(item, now = Date.now()) {
  const { performed_at: performedAt, ...rest } = item;
  for (const field of LOCAL_FIELDS) delete rest[field];
  const at = Date.parse(performedAt ?? "");
  if (Number.isNaN(at) || now - at < BACKDATE_AFTER_MS) return rest;
  return { ...rest, performed_at: performedAt };
}

/**
 * A queued set in words, for saying which set mono refused: its `label`
 * ("Bench press 135 lb × 8 @ 2 in reserve", written when it was logged), or
 * — for a set queued before labels — the exercise and the numbers.
 */
export function queuedSetText(item) {
  if (typeof item?.label === "string" && item.label.trim()) return item.label;
  const name = String(item?.exercise ?? "a set").replace(/[-_]/g, " ");
  const load = item?.band ? `${item.band} band` : (item?.load ?? "?");
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${load} × ${item?.reps ?? "?"}`;
}

const instant = (iso) => {
  const t = Date.parse(iso ?? "");
  return Number.isNaN(t) ? null : t;
};

/**
 * The newest activity this copy of the session shows: its last_activity_at,
 * its start, or its newest set (a set-log answer carries the sets, not the
 * session's new last_activity_at). ms, or null when it shows none.
 */
export function lastActivity(session) {
  const times = [session?.last_activity_at, session?.started_at, ...(session?.sets ?? []).map((s) => s.performed_at)]
    .map(instant)
    .filter((t) => t !== null);
  return times.length ? Math.max(...times) : null;
}

/**
 * When the session closed itself (D18), as an ISO time — or null while it's
 * open (or was finished on purpose). The server says so (`auto_closed`);
 * an open copy left on screen says so by the clock: idle SESSION_IDLE_MS
 * since its last activity, it closed at that activity (as the server's
 * next read or write will make true).
 */
export function closedItselfAt(session, now = Date.now()) {
  if (!session) return null;
  if (session.session_state === "auto_closed") {
    return session.finished_at ?? session.last_activity_at ?? null;
  }
  if ((session.session_state ?? "open") !== "open" || session.finished_at) return null;
  const last = lastActivity(session);
  if (last === null || now - last < SESSION_IDLE_MS) return null;
  return new Date(last).toISOString();
}

// --- pain today (walkthrough 6) ---------------------------------------------

// backend/training/constants.py JOINT_CHOICES.
export const JOINTS = [
  { value: "shoulder", label: "Shoulder" },
  { value: "elbow", label: "Elbow" },
  { value: "wrist", label: "Wrist" },
  { value: "knee", label: "Knee" },
  { value: "hip", label: "Hip" },
  { value: "low_back", label: "Low back" },
];

/** "shoulder", "low back" */
export const jointWords = (joint) => (JOINTS.find((j) => j.value === joint)?.label ?? String(joint)).toLowerCase();

/** "your left shoulder", "your low back" */
export function jointPlace(joint, side) {
  const sided = side === "left" || side === "right" ? `${side} ` : "";
  return `your ${sided}${jointWords(joint)}`;
}

/**
 * The joints that hurt today, as `Map(joint → side)`: the session's
 * check-in ("Any joint pain? Yes — …"), its pain reports (the API's
 * `pain_reports`) and any reported on this screen since. A joint reported
 * on both sides (or once as both) is "both".
 */
export function painfulJoints(session, extra = []) {
  const joints = new Map();
  const rows = [
    ...(Array.isArray(session?.checkin?.joint_pain) ? session.checkin.joint_pain : []),
    ...(session?.pain_reports ?? []),
    ...(extra ?? []),
  ];
  for (const row of rows) {
    if (!row?.joint) continue;
    const side = row.side ?? "both";
    const seen = joints.get(row.joint);
    joints.set(row.joint, seen === undefined || seen === side ? side : "both");
  }
  return joints;
}

/** The first joint `exercise` loads that hurts today: `{joint, side}`, or null. */
export function painfulJointOf(exercise, painful) {
  for (const joint of exercise?.joints ?? []) {
    if (painful?.has(joint)) return { joint, side: painful.get(joint) };
  }
  return null;
}

/**
 * How a failed request reads (as the planner's planSession.failureKind):
 * "offline" (no answer, or the server failed — try again), "throttled",
 * "signed-out" (401: the interceptor signs the person out; the sets wait on
 * this device for the same person), "gone" (404: the session isn't there),
 * or "refused" (any other 4xx: sending it again can't help).
 */
export function failureKind(err) {
  const code = err?.response?.status;
  if (!code || code >= 500) return "offline";
  if (code === 429) return "throttled";
  if (code === 401) return "signed-out";
  if (code === 404) return "gone";
  return "refused";
}

/** How long a 429 asks to wait (Retry-After seconds), else THROTTLE_MS. */
export function throttleWaitMs(err) {
  const seconds = Number(err?.response?.headers?.["retry-after"]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : THROTTLE_MS;
}

/**
 * Which set of a batch (0-based) a refusal names, or null when it doesn't
 * say: the server's own sentence ("Set 2: no exercise …") or a field error
 * on one set (`sets.1.reps`, flattened by parseApiError).
 */
export function refusedIndex(err, count) {
  const { formError, fieldErrors } = parseApiError(err);
  const fromText = /^Set (\d+):/.exec(formError ?? "");
  let index = fromText ? Number(fromText[1]) - 1 : null;
  if (index === null) {
    for (const path of Object.keys(fieldErrors)) {
      const match = /^sets\.(\d+)(?:\.|$)/.exec(path);
      if (match) {
        index = Number(match[1]);
        break;
      }
    }
  }
  return index !== null && index >= 0 && index < count ? index : null;
}

/** A refusal in words, without the "Set N:" the person can't see. */
export function refusalText(err, fallback = "mono refused this set.") {
  const { formError, fieldErrors } = parseApiError(err, fallback);
  const fields = Object.entries(fieldErrors).map(([path, value]) => {
    const field = path.replace(/^sets\.\d+\.?/, "").replace(/_/g, " ");
    const text = [].concat(value).join(" ");
    return field ? `${field}: ${text}` : text;
  });
  const text = [formError, ...fields].filter(Boolean).join(" ").replace(/^Set \d+:\s*/, "");
  return text || fallback;
}

/**
 * Fractional sets per muscle (D6: 1 for a primary muscle, 0.5 for a
 * secondary), from the catalog's weights: `[{muscle, label, sets}]`, most
 * first, then in the catalog's muscle order. Sets of an exercise the
 * catalog doesn't know count for nothing (no weights to go on).
 */
export function muscleSets(sets, byKey) {
  const totals = new Map();
  for (const set of sets ?? []) {
    const muscles = byKey?.get(set.exercise)?.muscles ?? {};
    for (const [muscle, weight] of Object.entries(muscles)) {
      const w = Number(weight);
      if (w > 0) totals.set(muscle, (totals.get(muscle) ?? 0) + w);
    }
  }
  return [...totals.entries()]
    .map(([muscle, total]) => ({ muscle, label: muscleLabel(muscle), sets: tidy(total) }))
    .sort(
      (a, b) =>
        b.sets - a.sets || (MUSCLE_ORDER.get(a.muscle) ?? 99) - (MUSCLE_ORDER.get(b.muscle) ?? 99),
    );
}

/** The muscles to ask pump and workload for: ≥ 2 fractional sets, catalog order. */
export function feedbackMuscles(sets, byKey) {
  return muscleSets(sets, byKey)
    .filter((row) => row.sets >= FEEDBACK_MIN_SETS)
    .sort((a, b) => (MUSCLE_ORDER.get(a.muscle) ?? 99) - (MUSCLE_ORDER.get(b.muscle) ?? 99));
}

/**
 * One set's estimated max (Epley on reps + reps in reserve, as the engine
 * reads it: an unlogged reserve counts as 2, past 12 counts as 12), on the
 * effective load — for a bodyweight move, the bodyweight it was logged at
 * plus the added weight less any band's help (D17). null when there's
 * nothing to go on (no reps, a band move, a bodyweight move without a
 * bodyweight).
 */
export function setE1rm(set, exercise) {
  const reps = Number(set?.reps);
  if (!(reps > 0)) return null;
  const kind = exercise?.load_kind ?? "external";
  if (kind === "band") return null;
  let load = Number(set.load ?? 0);
  if (kind === "bodyweight") {
    const bodyweight = Number(set.bodyweight);
    if (!(bodyweight > 0)) return null;
    load = bodyweight + load - Number(set.band_assist ?? 0);
  }
  if (!(load > 0)) return null;
  const rir = set.rir === null || set.rir === undefined ? UNKNOWN_RIR : Number(set.rir);
  return tidy(epley(load, Math.min(reps + rir, E1RM_MAX_REPS)));
}

/**
 * Each main lift's best estimated max in these sets (the engine keeps an
 * estimated max for main lifts): `[{exercise, name, e1rm, set}]` in the
 * order the lifts were first done. `set` is the one it came from — the
 * reason for the number.
 */
export function e1rmBests(sets, byKey) {
  const best = new Map();
  for (const set of sets ?? []) {
    const exercise = byKey?.get(set.exercise);
    if (exercise?.role !== "main") continue;
    const e1rm = setE1rm(set, exercise);
    if (e1rm === null) continue;
    const seen = best.get(set.exercise);
    if (!seen || e1rm > seen.e1rm) {
      best.set(set.exercise, { exercise: set.exercise, name: exercise.name, e1rm, set });
    }
  }
  return [...best.values()];
}
